// Side-by-side horizontal bar charts: the 10 largest insider Purchases and
// Sales by dollar value within the board (already liquidity-screened).

import { useMemo } from 'react';

import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { TopLiquidInsiderRow } from '../api/insiderTradingApi';
import { formatCompactUsd } from '../utils/formatters';

interface InsiderValueChartProps {
    purchases: TopLiquidInsiderRow[];
    sales: TopLiquidInsiderRow[];
}

interface Point {
    symbol: string;
    value: number;
    insiders: number;
}

const toPoints = (rows: TopLiquidInsiderRow[]): Point[] =>
    [...rows]
        .sort((a, b) => b.total_value - a.total_value)
        .slice(0, 10)
        .map((r) => ({ symbol: r.symbol, value: r.total_value, insiders: r.insider_count }));

const ChartTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: Array<{ payload: Point }>;
}) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 shadow-lg text-xs">
            <div className="font-bold text-slate-900 dark:text-white">{p.symbol}</div>
            <div className="font-mono">{formatCompactUsd(p.value)}</div>
            <div className="text-slate-500">
                {p.insiders} insider{p.insiders === 1 ? '' : 's'}
            </div>
        </div>
    );
};

const SideChart = ({ title, data, color }: { title: string; data: Point[]; color: string }) => (
    <div className="flex-1 min-w-0">
        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</h4>
        {data.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">
                No data
            </div>
        ) : (
            <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.25} />
                        <XAxis
                            type="number"
                            tickFormatter={(v: number) => formatCompactUsd(v)}
                            tick={{ fontSize: 10 }}
                        />
                        <YAxis
                            type="category"
                            dataKey="symbol"
                            width={56}
                            tick={{ fontSize: 11, fontWeight: 700 }}
                        />
                        <Tooltip content={<ChartTooltip />} cursor={{ fillOpacity: 0.08 }} />
                        <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} isAnimationActive={false} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        )}
    </div>
);

export const InsiderValueChart = ({ purchases, sales }: InsiderValueChartProps) => {
    const buyData = useMemo(() => toPoints(purchases), [purchases]);
    const sellData = useMemo(() => toPoints(sales), [sales]);

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5">
            <h3 className="text-base font-black tracking-tight text-slate-900 dark:text-white mb-4">
                Largest Insider Dollar Flows
            </h3>
            <div className="flex flex-col lg:flex-row gap-6">
                <SideChart title="Purchases" data={buyData} color="#10b981" />
                <SideChart title="Sales" data={sellData} color="#f43f5e" />
            </div>
        </div>
    );
};

export default InsiderValueChart;
